const REFUEL_JUMP_THRESHOLD = 10;
const MIN_BASIS_LAPS = 2;
const MIN_LAP_TIME_SAMPLES = 2;

export const RECENT_LAP_TIMES = 5;

export interface FuelBaseline {
  lapCount: number;
  fuel: number;
}

/**
 * Detect when the fuel-projection baseline must reset: first reading, or a
 * fuel-level jump upward large enough to indicate a refuel.
 */
export function nextFuelBaseline(
  prev: FuelBaseline | null,
  lapCount: number,
  fuel: number,
): FuelBaseline {
  if (!prev || fuel > prev.fuel + REFUEL_JUMP_THRESHOLD) {
    return { lapCount, fuel };
  }
  return prev;
}

/**
 * Project laps remaining from *observed* consumption since the baseline
 * (session start or last refuel). Returns null until at least
 * MIN_BASIS_LAPS laps of real burn data exist — never guesses from an
 * assumed full tank.
 */
export function computeFuelProjection(
  baseline: FuelBaseline | null,
  lapCount: number,
  fuel: number | null,
): { lapsRemaining: number; basisLaps: number } | null {
  if (!baseline || fuel === null) return null;
  const basisLaps = lapCount - baseline.lapCount;
  if (basisLaps < MIN_BASIS_LAPS) return null;
  const consumed = baseline.fuel - fuel;
  if (consumed <= 0) return null;
  const fuelPerLap = consumed / basisLaps;
  return { lapsRemaining: Math.floor(fuel / fuelPerLap), basisLaps };
}

/**
 * Minutes of running left at the rolling mean of the most recent lap times.
 * Null until at least MIN_LAP_TIME_SAMPLES completed laps are known.
 */
export function estimateMinutesRemaining(
  lapsRemaining: number | null,
  recentLapTimesMs: readonly number[],
): number | null {
  if (lapsRemaining === null) return null;
  const samples = recentLapTimesMs.filter((ms) => Number.isFinite(ms) && ms > 0).slice(-RECENT_LAP_TIMES);
  if (samples.length < MIN_LAP_TIME_SAMPLES) return null;
  const avgLapMs = samples.reduce((sum, ms) => sum + ms, 0) / samples.length;
  return Math.round((lapsRemaining * avgLapMs) / 60_000);
}

/**
 * Laps completed for fuel-burn purposes. RaceChrono's lap counter is the
 * authoritative source when it is publishing; the GPS finish-line timer is
 * the fallback when a finish line is configured. Null means no lap source is
 * live yet — callers must not establish a fuel baseline against it, or a
 * counter that later jumps from 0 to N makes the burn rate look tiny.
 */
export function effectiveLapCount(
  raceChronoLapNumber: number | null,
  gpsLapCount: number,
  gpsTimerConfigured: boolean,
): number | null {
  if (raceChronoLapNumber !== null && raceChronoLapNumber > 0) return raceChronoLapNumber;
  return gpsTimerConfigured ? gpsLapCount : null;
}
